import styles from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal.module.scss';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarketDialog, MarketDivider } from '@market/react';
import { captureException } from '@sentry/react';
import {
  FieldValues,
  UseFormHandleSubmit,
  useFormContext,
} from 'react-hook-form';
import { trackCdp } from 'utilities/tracking';
import { ReactComponent as RecaptchaHeaderImage } from 'images/modal_headers/recaptcha_header.svg';
import RecaptchaWorkaround from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_workaround';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useCompleteOrder,
  useGetOrderSummary,
  useRequestRecaptchaFallback,
} from 'typescript/hooks/use_order_actions_client';
import {
  CHECKOUT_ROUTES,
  CompleteOrderError,
} from 'typescript/constants/enums/checkout';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import { COMPONENT_NAMES, FEATURES } from 'typescript/constants/tracking';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';
import {
  normalizeCheckoutError,
  normalizeCompleteOrderError,
} from 'typescript/utilities/checkout_error_telemetry';

type RecaptchaFormModalProps = {
  handleFormSubmit: UseFormHandleSubmit<FieldValues, undefined>;
  onRecaptchaVerified?: never;
  onRecaptchaCancelled?: never;
};

type RecaptchaCallbackModalProps = {
  handleFormSubmit?: never;
  onRecaptchaVerified: (recaptchaResponse: string) => void;
  onRecaptchaCancelled: () => void;
};

type RecaptchaModalProps =
  | RecaptchaFormModalProps
  | RecaptchaCallbackModalProps;

const RecaptchaModal = ({
  handleFormSubmit,
  onRecaptchaVerified,
  onRecaptchaCancelled,
}: RecaptchaModalProps) => {
  const { t } = useTranslation();
  const { languageCode, countryCode } = useLocaleInfo();
  const formContext = useFormContext<FieldValues>();
  const { recaptchaKey, orderNumber, trackingData } = useCheckoutFlow();
  const { completeOrder } = useCompleteOrder({ orderNumber });
  const { requestRecaptchaFallback } = useRequestRecaptchaFallback({
    orderNumber,
    countryCode,
    languageCode,
  });

  const {
    showRecaptcha,
    hasPendingCompleteOrder,
    setShowRecaptcha,
    isCompletingOrder,
    setCompleteOrderError,
  } = useCompleteOrderContext();

  const history = useHistory();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });

  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    orderNumber,
    orderSummaryData,
  });

  const recaptchaAttemptRef = useRef(0);
  const isRecaptchaActiveRef = useRef(showRecaptcha);
  const recaptchaAttempt = recaptchaAttemptRef.current;

  useEffect(() => {
    isRecaptchaActiveRef.current = showRecaptcha;

    return () => {
      isRecaptchaActiveRef.current = false;
    };
  }, [showRecaptcha]);

  // Reset the form's touched/dirty state when dismissing the recaptcha modal
  // so the delivery page CTA re-enables. The forced blur in handleSubmitWithLogging
  // marks a field as touched, which disables the CTA via the canContinue check.
  const dismissRecaptcha = () => {
    // Invalidate the attempt before changing UI state. The fallback request is
    // asynchronous and must not be allowed to complete an order after cancel.
    isRecaptchaActiveRef.current = false;
    recaptchaAttemptRef.current += 1;
    onRecaptchaCancelled?.();

    // Callback consumers do not rely on form reset; delivery consumers still
    // get the form reset behavior.
    if (formContext) {
      formContext.reset(formContext.getValues());
    }
    setShowRecaptcha(false);
  };

  const onCompleteOrder = (recaptchaResponse: string) => (_event) => {
    if (isCompletingOrder) {
      return;
    }

    completeOrder(
      { recaptchaResponse },
      {
        onSuccess: (response) => {
          logCompleteOrderAction({
            status: 'success',
            telemetry: response?.telemetry,
          });
          setShowRecaptcha(false);
        },
        onError: (error) => {
          const normalizedError = normalizeCompleteOrderError(error);

          trackCdp({
            name: CDP_EVENT_NAMES.CHECKOUT_V3.DELIVERY
              .COMPLETING_ORDER_ERROR_DELIVERY,
            params: {
              feature: FEATURES.SHOP.NAME,
              step: COMPONENT_NAMES.APPS.CHECKOUT_V3.DELIVERY,
              error: error?.message,
              error_code: normalizedError.errorCode,
              ...trackingData,
            },
          });

          logCompleteOrderAction({
            status: 'error',
            errorStage: 'complete_order',
            ...normalizedError,
          });
          setCompleteOrderError(CompleteOrderError.GENERIC);
          setShowRecaptcha(false);
        },
        onSettled: (_, error) => {
          // if there is no error, we should redirect to the complete page
          if (!error) {
            history.push(CHECKOUT_ROUTES.COMPLETE);
          }
        },
      },
    );
  };

  const handleSubmit = (recaptchaResponse: string) => {
    if (
      !isRecaptchaActiveRef.current ||
      recaptchaAttemptRef.current !== recaptchaAttempt
    ) {
      return;
    }

    if (onRecaptchaVerified) {
      onRecaptchaVerified(recaptchaResponse);
      return;
    }

    handleFormSubmit(onCompleteOrder(recaptchaResponse))();
  };

  if (!showRecaptcha || (!onRecaptchaVerified && hasPendingCompleteOrder)) {
    return null;
  }

  return (
    <MarketDialog className={styles.dialog} isLoading={false} persistent={true}>
      <div className="shop-grid-container">
        <div className="shop-grid-item-full">
          <RecaptchaHeaderImage />
        </div>
        <h2 className="shop-grid-item-full">
          {t('checkout.dialog.recaptcha.title')}
        </h2>
        <p className="shop-grid-item-full">
          {t('checkout.dialog.recaptcha.body')}
        </p>
        <RecaptchaWorkaround
          className={`${styles.recaptchaContainer} shop-grid-item-full`}
          languageCode={languageCode}
          siteKey={recaptchaKey}
          onVerify={handleSubmit}
          onError={async (recaptchaError?: Error) => {
            captureException(recaptchaError, {
              tags: {
                orderNumber,
              },
            });

            let fallbackToken: string;
            try {
              // try to get a server-generated challenge token
              fallbackToken = await requestRecaptchaFallback();
            } catch (fallbackError) {
              if (
                !isRecaptchaActiveRef.current ||
                recaptchaAttemptRef.current !== recaptchaAttempt
              ) {
                return;
              }

              // if fallback challenge fails, capture error and close modal
              captureException(fallbackError, {
                tags: {
                  orderNumber,
                },
              });

              // The system failed, not the buyer, so this is an error. The
              // cause is the status or network tag, never the message, which
              // can quote the response body.
              const fallbackFailure = normalizeCheckoutError({
                error: fallbackError,
              });
              const { invalid_response: invalidResponse } = (fallbackError ??
                {}) as { invalid_response?: boolean };
              // Not awaited (it never rejects), so the dismissal below runs in
              // the same tick as the attempt guard above.
              logCompleteOrderAction({
                status: 'error',
                errorStage: 'complete_order',
                errorCode: 'recaptcha_failed',
                errorDetails: [
                  'recaptcha_fallback_failure',
                  invalidResponse && 'invalid_response',
                  fallbackFailure.errorDetails ?? fallbackFailure.errorCode,
                ]
                  .filter(Boolean)
                  .join(', '),
              });

              // Show error and close the recaptcha modal
              setCompleteOrderError(CompleteOrderError.GENERIC);
              dismissRecaptcha();
              return;
            }

            // Outside the try, so a failure while completing the order is
            // never reported as a fallback failure.
            if (
              !isRecaptchaActiveRef.current ||
              recaptchaAttemptRef.current !== recaptchaAttempt
            ) {
              return;
            }
            handleSubmit(fallbackToken);
          }}
        />
        <MarketDivider
          className={`${styles.removeMargin} shop-grid-item-full`}
          size="thin"
        />
        <p className="shop-grid-item-full">
          {`${t('checkout.dialog.notReady')} `}
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              dismissRecaptcha();
              await logCompleteOrderAction({ status: 'cancelled' });
            }}
          >
            {t('checkout.dialog.cancelCTA')}
          </a>
        </p>
      </div>
    </MarketDialog>
  );
};

export default RecaptchaModal;
